import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { X } from 'lucide-react'
import { boundingExtent, createEmpty, extend, isEmpty as isEmptyExtent } from 'ol/extent'
import { fromLonLat } from 'ol/proj'

import { MapProvider, useMap } from '@/lib/ol-cleaned-lib'

import { BasemapPopover } from './BasemapPopover'
// import { GisSearch } from './GisSearch' // map search hidden — the top-left spot is the ✕ close-map button now
import { LayersPopover, type LayerToggles } from './LayersPopover'
// import { LensChip } from './LensChip' // lens dropdown hidden for demo — restore with the <LensChip/> usage below
import { MapControlCluster } from './MapControlCluster'
import { MapLegend } from './MapLegend'
import { buildBasemapLayers, type BasemapKey } from '../layers/basemaps'
import { buildDistrictOutlineLayer, DISTRICT_LAYER_ID } from '../layers/districtOutlineLayer'
import { buildMandalLayer, mandalStyleFn, type MapMetric } from '../layers/mandalChoroplethLayer'
import { buildSiteLayer, SITE_LAYER_ID, siteStyleFn } from '../layers/siteMarkersLayer'
import { filterSitesByStatus } from '../lib/coverage'
import { filterSitesWithIssues } from '../lib/issues'
import { LENSES } from '../lib/lens'
import { prettyName } from '../lib/mandal-name'
import { siteClusterTip } from '../lib/map-tooltip'
import { useGisCoverageView } from '../stores/gisCoverageViewStore'
import { useGisIssueView } from '../stores/gisIssueViewStore'

import type { GisLens, GisOverview, GisSite } from '../api/types'
import type { GisSelection } from '../hooks/useGisSelection'
import type { SiteIssueCount } from '../lib/issues'
import type { MapBrowserEvent } from 'ol'
import type Feature from 'ol/Feature'
import type Layer from 'ol/layer/Layer'
import type VectorLayer from 'ol/layer/Vector'
import type VectorSource from 'ol/source/Vector'

const YADADRI_CENTER: [number, number] = [79.07, 17.43]
const MANDALS_URL = '/geo/yadadri-mandals.geojson'
const DISTRICT_URL = '/geo/yadadri-district.geojson'
const FIT_PADDING = [80, 80, 80, 80]

/** Union extent of every mandal feature whose `Mandal` key is in `names`; null when none matched. */
function mandalUnionExtent(features: Feature[], names: ReadonlySet<string>): number[] | null {
  const union = createEmpty()
  for (const f of features) {
    if (names.has(f.get('Mandal') as string)) {
      const ext = f.getGeometry()?.getExtent()
      if (ext) {
        extend(union, ext)
      }
    }
  }
  return isEmptyExtent(union) ? null : union
}

type MapEvtHandler = (evt: MapBrowserEvent<PointerEvent>) => void
// OL's listener typings for these events are internally inconsistent (the
// MapBrowserEvent generic constraint excludes UIEvent). Bind via a narrow interface.
interface EvtBindable {
  on(type: 'singleclick' | 'pointermove', listener: MapEvtHandler): void
  un(type: 'singleclick' | 'pointermove', listener: MapEvtHandler): void
}

export interface GisMapPaneProps {
  overview: GisOverview
  lens: GisLens
  /** What the choropleth shades by: attention (Overview), issue count (Issues), or coverage/officer %. */
  mapMetric: MapMetric
  /** Pre-computed attention score per mandal — the Overview choropleth fill + labels. */
  attentionByName: Map<string, number>
  /** Filter-reactive issue count per mandal — the Issues choropleth fill + labels. 0/absent → grey. */
  issueCountByName: Map<string, number>
  /** True when the issues feed is capped (`hasMore`) — surfaces a "first N" note so counts aren't silently partial. */
  issuesCapped: boolean
  /** Server's total issue count for the window — shown in the cap note when `issuesCapped`. */
  issuesTotalCount: number
  /** Active open/critical issue counts per site id — drives the "Issue hotspots" ring overlay (B1b). */
  issuesBySite: Map<string, SiteIssueCount>
  selection: GisSelection
  /** Page-level mandal filter (multi-select) — highlights every selected polygon + fits to their union. */
  activeMandals: readonly string[]
  /** Issues tab active: filter markers to sites-with-issues and force the severity ring overlay on. */
  issuesFocus: boolean
  /** Coverage tab active: the Explore Coverage status facet filters the site markers. */
  coverageFocus: boolean
  onLensChange: (lens: GisLens) => void
  onSelectMandal: (name: string) => void
  onSelectSite: (id: string) => void
  onClearSelection: () => void
  /** Close (hide) the map — the ✕ button in the top-left; wired to the Split/Full toggle. */
  onCloseMap: () => void
}

function MapCanvas(props: GisMapPaneProps) {
  const { overview, lens, mapMetric, attentionByName, issueCountByName, selection, issuesFocus, coverageFocus } = props
  const { map } = useMap()
  const hoveredSiteId = useGisIssueView((s) => s.hoveredSiteId)
  const focusedSiteId = useGisIssueView((s) => s.focusedSiteId)
  const covStatusFilter = useGisCoverageView((s) => s.statusFilter)
  const paneRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<HTMLDivElement>(null)
  const tipRef = useRef<HTMLDivElement>(null)
  const mandalLayerRef = useRef<VectorLayer<VectorSource> | null>(null)
  const districtLayerRef = useRef<VectorLayer<VectorSource> | null>(null)
  const labelElsRef = useRef<Map<string, HTMLDivElement>>(new Map())

  const [toggles, setToggles] = useState<LayerToggles>({
    district: true,
    mandal: true,
    sites: true,
    shade: false,
    issues: false,
    legend: false,
  })
  const [basemap, setBasemap] = useState<BasemapKey>('streets')
  const [popover, setPopover] = useState<'layers' | 'basemap' | null>(null)
  const [labelsReady, setLabelsReady] = useState(false)
  const [layersReady, setLayersReady] = useState(false)

  const selectedMandalSet = useMemo(() => new Set(props.activeMandals), [props.activeMandals])
  const selectedSiteId = selection?.kind === 'site' ? selection.id : null
  // On the Issues tab the rings are forced on; elsewhere they follow the manual "Issue hotspots" toggle.
  const effectiveIssues = issuesFocus || toggles.issues
  const issueOverlay = effectiveIssues ? props.issuesBySite : undefined
  // Markers: every site normally; on the Issues tab only sites present in the (filtered) hotspot map;
  // on the Coverage tab only sites matching the Explore Coverage status facet (when one is active).
  const visibleSites = useMemo(
    () =>
      filterSitesByStatus(
        filterSitesWithIssues(overview.sites, props.issuesBySite, issuesFocus),
        covStatusFilter,
        coverageFocus,
      ),
    [overview.sites, props.issuesBySite, issuesFocus, covStatusFilter, coverageFocus],
  )

  // Mount target + keep OL sized to the pane (drag, collapse, window resize — one mechanism).
  useEffect(() => {
    if (!map || !mapRef.current || !paneRef.current) {
      return
    }
    map.setTarget(mapRef.current)
    map.updateSize()
    const ro = new ResizeObserver(() => map.updateSize())
    ro.observe(paneRef.current)
    return () => {
      ro.disconnect()
      map.setTarget(undefined)
    }
  }, [map])

  // Build static layers once: basemaps + mandal polygons + district outline, then fit.
  useEffect(() => {
    if (!map) {
      return
    }
    // `cancelled` + the per-run `basemaps` local are load-bearing for Strict Mode
    // double-mount: each run must own its own instances or layers double-add.
    let cancelled = false
    const basemaps = buildBasemapLayers()
    Object.values(basemaps).forEach((l) => map.addLayer(l))
    void Promise.all([buildMandalLayer(MANDALS_URL), buildDistrictOutlineLayer(DISTRICT_URL)]).then(
      ([mandalLayer, districtLayer]) => {
        if (cancelled) {
          return
        }
        map.addLayer(mandalLayer)
        // First paint: style immediately — the restyle effect can't see the ref being set.
        mandalLayer.setStyle(
          mandalStyleFn({
            metric: mapMetric,
            mandals: overview.mandals,
            attentionByName,
            issueCountByName,
            selectedNames: selectedMandalSet,
            shade: toggles.shade,
          }),
        )
        map.addLayer(districtLayer)
        mandalLayerRef.current = mandalLayer
        districtLayerRef.current = districtLayer
        setLayersReady(true)
        const extent = districtLayer.getSource()?.getExtent()
        if (extent) {
          map.getView().fit(extent, { padding: FIT_PADDING, duration: 0 })
        }
      },
    )
    return () => {
      cancelled = true
      Object.values(basemaps).forEach((l) => map.removeLayer(l))
      if (mandalLayerRef.current) {
        map.removeLayer(mandalLayerRef.current)
      }
      if (districtLayerRef.current) {
        map.removeLayer(districtLayerRef.current)
      }
      mandalLayerRef.current = null
      districtLayerRef.current = null
    }
  }, [map])

  // Choropleth style: metric / attention / issue count / data / selection / shade.
  useEffect(() => {
    const layer = mandalLayerRef.current
    if (!layer) {
      return
    }
    layer.setStyle(
      mandalStyleFn({
        metric: mapMetric,
        mandals: overview.mandals,
        attentionByName,
        issueCountByName,
        selectedNames: selectedMandalSet,
        shade: toggles.shade,
      }),
    )
  }, [map, mapMetric, attentionByName, issueCountByName, overview, selectedMandalSet, toggles.shade])

  // Sites: rebuild on data/filter change, restyle on selection/hover change.
  useEffect(() => {
    if (!map) {
      return
    }
    const layer = buildSiteLayer(visibleSites)
    layer.setStyle(siteStyleFn(selectedSiteId, hoveredSiteId, issueOverlay, focusedSiteId))
    layer.setVisible(toggles.sites)
    map.addLayer(layer)
    return () => {
      map.removeLayer(layer)
    }
    // selectedSiteId/hover/toggles handled below without a rebuild.
  }, [map, visibleSites])

  useEffect(() => {
    if (!map) {
      return
    }
    const layer = map
      .getLayers()
      .getArray()
      .find((l) => l.get('id') === SITE_LAYER_ID) as VectorLayer<VectorSource> | undefined
    layer?.setStyle(siteStyleFn(selectedSiteId, hoveredSiteId, issueOverlay, focusedSiteId))
    layer?.setVisible(toggles.sites)
  }, [map, selectedSiteId, hoveredSiteId, focusedSiteId, toggles.sites, effectiveIssues, props.issuesBySite])

  // Boundary visibility toggles.
  useEffect(() => {
    mandalLayerRef.current?.setVisible(toggles.mandal)
    labelElsRef.current.forEach((el) => {
      el.style.display = toggles.mandal ? '' : 'none'
    })
    districtLayerRef.current?.setVisible(toggles.district)
  }, [toggles.mandal, toggles.district])

  // Basemap switching.
  useEffect(() => {
    if (!map) {
      return
    }
    for (const key of ['streets', 'satellite', 'terrain'] as const) {
      const layer = map
        .getLayers()
        .getArray()
        .find((l) => l.get('id') === `gis-basemap-${key}`)
      layer?.setVisible(key === basemap)
    }
  }, [map, basemap])

  // Mandal label chips (OL overlays over centroids).
  useEffect(() => {
    if (!map) {
      return
    }
    // OL Overlay is imported lazily here to keep the static import list tidy.
    let disposed = false
    const els = labelElsRef.current
    void import('ol/Overlay').then(({ default: Overlay }) => {
      if (disposed) {
        return
      }
      overview.mandals.forEach((m) => {
        if (els.has(m.name)) {
          return
        }
        const el = document.createElement('div')
        el.className =
          'cursor-pointer rounded-full border border-slate-900/15 bg-white/92 px-2 py-0.5 text-[11px] font-bold whitespace-nowrap shadow'
        el.setAttribute('role', 'button')
        el.setAttribute('tabindex', '0')
        el.setAttribute('aria-label', `${prettyName(m.name)} mandal`)
        const openMandal = (): void => props.onSelectMandal(m.name)
        el.addEventListener('click', openMandal)
        el.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            openMandal()
          }
        })
        map.addOverlay(new Overlay({ element: el, position: fromLonLat([...m.centroid]), positioning: 'center-center' }))
        els.set(m.name, el)
      })
      setLabelsReady(true) // signal the text effect: elements exist now (async import resolved after its first run)
    })
    return () => {
      disposed = true
    }
    // Overlays persist for the map's lifetime; text refreshes in the next effect.
  }, [map, overview])

  // Label text follows the active fill: the attention score (Overview), the filter-reactive issue
  // count (Issues), or the lens % (Coverage/Officers). `labelsReady` re-runs this once the async
  // overlay creation lands.
  useEffect(() => {
    const L = LENSES[lens]
    overview.mandals.forEach((m) => {
      const el = labelElsRef.current.get(m.name)
      if (!el) {
        return
      }
      const text =
        mapMetric === 'attention'
          ? String(attentionByName.get(m.name) ?? 0)
          : mapMetric === 'issues'
            ? String(issueCountByName.get(m.name) ?? 0)
            : L.fmt(L.value(m))
      el.innerHTML = `<b>${prettyName(m.name)}</b> · ${text}`
    })
  }, [lens, mapMetric, attentionByName, issueCountByName, overview, labelsReady])

  // Hover tooltip + cursor — site/cluster markers (with issue counts) and mandals.
  useEffect(() => {
    if (!map) {
      return
    }
    const mandalTip = (name: string): string | null => {
      const m = overview.mandals.find((x) => x.name === name)
      if (!m) {
        return null
      }
      const L = LENSES[lens]
      const label = mapMetric === 'attention' ? 'Attention' : mapMetric === 'issues' ? 'Issues' : L.label
      const value =
        mapMetric === 'attention'
          ? String(attentionByName.get(m.name) ?? 0)
          : mapMetric === 'issues'
            ? String(issueCountByName.get(m.name) ?? 0)
            : L.fmt(L.value(m))
      return `<b>${prettyName(m.name)}</b><br>${label}: ${value}`
    }
    const handler: MapEvtHandler = (evt) => {
      const tip = tipRef.current
      if (!tip) {
        return
      }
      if (evt.dragging) {
        tip.hidden = true
        return
      }
      const hit = map.forEachFeatureAtPixel(evt.pixel, (f) => f, {
        hitTolerance: 4,
        layerFilter: (l: Layer) => l.get('id') !== DISTRICT_LAYER_ID,
      })
      map.getTargetElement().style.cursor = hit ? 'pointer' : ''
      const members = hit?.get('features') as Feature[] | undefined
      let html: string | null = null
      if (members) {
        html = siteClusterTip(members, issueOverlay)
      } else if (hit) {
        html = mandalTip(hit.get('Mandal') as string)
      }
      if (!html) {
        tip.hidden = true
        return
      }
      // Hover tooltip disabled — the same bundle/site info is shown at the top of the map.
      // tip.hidden = false
      // tip.innerHTML = html
      // tip.style.left = `${evt.pixel[0]! + 12}px`
      // tip.style.top = `${evt.pixel[1]! + 12}px`
    }
    const bindable = map as unknown as EvtBindable
    bindable.on('pointermove', handler)
    return () => bindable.un('pointermove', handler)
  }, [map, lens, mapMetric, attentionByName, issueCountByName, overview, issueOverlay])

  // Click → site / cluster-zoom / mandal.
  useEffect(() => {
    if (!map) {
      return
    }
    const handler: MapEvtHandler = (evt) => {
      const hit = map.forEachFeatureAtPixel(evt.pixel, (f) => f, {
        hitTolerance: 4,
        layerFilter: (l: Layer) => l.get('id') !== DISTRICT_LAYER_ID,
      })
      if (!hit) {
        return
      }
      const members = hit.get('features') as Feature[] | undefined
      if (members) {
        if (members.length === 1) {
          props.onSelectSite((members[0]!.get('site') as GisSite).id)
        } else {
          const extent = boundingExtent(
            members.map((f) =>
              (f.getGeometry() as unknown as { getCoordinates(): number[] }).getCoordinates(),
            ),
          )
          map.getView().fit(extent, { padding: [120, 120, 120, 120], duration: 500, maxZoom: 13 })
        }
        return
      }
      const name = hit.get('Mandal') as string | undefined
      if (name) {
        props.onSelectMandal(name)
      }
    }
    const bindable = map as unknown as EvtBindable
    bindable.on('singleclick', handler)
    return () => bindable.un('singleclick', handler)
  }, [map, props.onSelectSite, props.onSelectMandal])

  // Pan/zoom the map to a site by id (no-op when the site is unknown or un-geocoded). Shared by the
  // selection fly-to (Locate pin / drill frames) and the issue-focus fly-to (clicking a board issue).
  const focusSite = useCallback(
    (siteId: string) => {
      if (!map) {
        return
      }
      const s = overview.sites.find((x) => x.id === siteId)
      if (s && s.lon !== null && s.lat !== null) {
        map.getView().animate({ center: fromLonLat([s.lon, s.lat]), zoom: 13, duration: 650 })
      }
    },
    [map, overview],
  )

  // Fly to the current selection (site / issue drill frame).
  useEffect(() => {
    if (!map || !selection) {
      return
    }
    if (selection.kind === 'site') {
      focusSite(selection.id)
      return
    }
    // Selecting an issue opens its panel drawer AND zooms the map to its site (when known), keeping the
    // selected issue and its geographic context in sync. No-ops gracefully when the issue has no site or
    // its site is outside the current window (focusSite finds nothing).
    if (selection.kind === 'issue') {
      if (selection.siteId) {
        focusSite(selection.siteId)
      }
      return
    }
  }, [map, selection, focusSite])

  // Fly to the site of the issue currently open in the board (clicking a List row or Gallery card).
  // Independent of the drill selection above, so it keeps the inline-detail UX and just moves the map.
  useEffect(() => {
    if (focusedSiteId) {
      focusSite(focusedSiteId)
    }
  }, [focusedSiteId, focusSite])

  // Page-level mandal filter → fit to the UNION extent of every selected polygon; clearing fits back to
  // the district. `layersReady` re-runs this once the async vector layers exist (handles a shared
  // ?mandal= cold load).
  useEffect(() => {
    if (!map || !layersReady) {
      return
    }
    if (selectedMandalSet.size > 0) {
      const union = mandalUnionExtent(
        mandalLayerRef.current?.getSource()?.getFeatures() ?? [],
        selectedMandalSet,
      )
      if (union) {
        map.getView().fit(union, { padding: FIT_PADDING, duration: 600, maxZoom: 12 })
      }
      return
    }
    const districtExtent = districtLayerRef.current?.getSource()?.getExtent()
    if (districtExtent) {
      map.getView().fit(districtExtent, { padding: FIT_PADDING, duration: 600 })
    }
  }, [map, selectedMandalSet, layersReady])

  const layerCount = useMemo(
    () =>
      [
        toggles.district,
        toggles.mandal,
        toggles.sites,
        toggles.shade,
        effectiveIssues,
        toggles.legend,
      ].filter(Boolean).length,
    [toggles, effectiveIssues],
  )

  const reset = () => {
    props.onClearSelection()
    const extent = districtLayerRef.current?.getSource()?.getExtent()
    if (map && extent) {
      map.getView().fit(extent, { padding: [60, 60, 60, 60], duration: 500 })
    }
  }

  return (
    <div ref={paneRef} className="relative h-full w-full">
      <div ref={mapRef} className="absolute inset-0 z-0" role="region" aria-label={`District map — ${overview.mandals.length} mandals, ${overview.sites.length} sites`} />
      <div ref={tipRef} hidden aria-hidden="true" className="pointer-events-none absolute z-[55] rounded-lg bg-slate-900 px-2 py-1.5 text-[11px] leading-relaxed text-white shadow-lg" />
      {/* Map search hidden per request — restore by uncommenting the import + this line:
      <GisSearch overview={overview} onSelectMandal={props.onSelectMandal} onSelectSite={props.onSelectSite} /> */}
      {/* ✕ close-map button in the search's old top-left spot — hides the map (switches to Full view). */}
      <button
        type="button"
        onClick={props.onCloseMap}
        title="Close map"
        aria-label="Close map"
        className="absolute top-[18px] left-[18px] z-20 grid h-[38px] w-[38px] place-items-center rounded-[12px] bg-gis-card text-gis-ink shadow-gis-pop ring-1 ring-[rgba(15,31,61,.06)] transition-colors hover:bg-gis-track focus-visible:ring-2 focus-visible:ring-blue-500/60"
      >
        <X className="h-[18px] w-[18px] stroke-[2.2]" />
      </button>
      {toggles.legend && <MapLegend lens={lens} metric={mapMetric} issuesOverlay={effectiveIssues} />}
      {mapMetric === 'issues' && props.issuesCapped && (
        <div
          className="pointer-events-none absolute top-3 left-1/2 z-30 -translate-x-1/2 rounded-full bg-amber-50 px-3 py-1 text-[11px] font-semibold text-amber-800 shadow ring-1 ring-amber-200"
          role="status"
        >
          Showing first 200 of {props.issuesTotalCount} — mandal counts may be partial
        </div>
      )}
      {/* Lens dropdown (Coverage % / Issues / Officers %) hidden for demo. Restore: uncomment the
          LensChip import above and the line below.
      <LensChip lens={lens} onChange={props.onLensChange} /> */}
      <MapControlCluster
        layerCount={layerCount}
        accent={LENSES[lens].accent}
        onToggleLayers={() => setPopover((p) => (p === 'layers' ? null : 'layers'))}
        onToggleBasemap={() => setPopover((p) => (p === 'basemap' ? null : 'basemap'))}
        onZoomIn={() => map?.getView().animate({ zoom: (map.getView().getZoom() ?? 10) + 1, duration: 200 })}
        onZoomOut={() => map?.getView().animate({ zoom: (map.getView().getZoom() ?? 10) - 1, duration: 200 })}
        onReset={reset}
      />
      {popover === 'layers' && (
        <LayersPopover
          toggles={toggles}
          onChange={(k, v) => setToggles((t) => ({ ...t, [k]: v }))}
          issuesFocus={issuesFocus}
        />
      )}
      {popover === 'basemap' && <BasemapPopover value={basemap} onChange={setBasemap} />}
    </div>
  )
}

export function GisMapPane(props: GisMapPaneProps) {
  return (
    <MapProvider center={YADADRI_CENTER} initialZoom={10} basemapUrl={null}>
      <MapCanvas {...props} />
    </MapProvider>
  )
}
